import { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal } from '@react-apps/design-system';
import {
  validateGuaranteeRule,
  type GuaranteeRule,
  type GuaranteeRuleErrors,
} from '../../domain/entities/guarantee.entity';
import {
  CAMPAIGN_CREATE_KEY,
  useCreateGuaranteeCampaign,
} from '../hooks/use-guarantee-campaigns';
import {
  DEFAULT_RULE_DRAFT,
  draftToRule,
  GuaranteeRuleFields,
  ruleToDraft,
} from './GuaranteeRuleFields';

interface GuaranteeCampaignFormModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * "Duplicar como campanha nova": a régua de uma campanha travada entra como
   * valor inicial. As ofertas não vêm — cada uma só cabe numa campanha.
   */
  initialRule?: GuaranteeRule;
}

export function GuaranteeCampaignFormModal({
  open,
  onClose,
  initialRule,
}: GuaranteeCampaignFormModalProps) {
  const busy = useIsMutating({ mutationKey: CAMPAIGN_CREATE_KEY }) > 0;
  // O `Modal` desmonta os filhos ao fechar: o formulário reabre limpo. Por isso
  // não fecha com a criação em voo — a campanha nasceria sem levar ao detalhe.
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={initialRule ? 'Duplicar como campanha nova' : 'Nova campanha'}
      description="As ofertas entram depois, no detalhe da campanha."
      size="lg"
    >
      <GuaranteeCampaignForm initialRule={initialRule} onClose={onClose} />
    </Modal>
  );
}

function GuaranteeCampaignForm({
  initialRule,
  onClose,
}: {
  initialRule?: GuaranteeRule;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const create = useCreateGuaranteeCampaign();
  const [name, setName] = useState('');
  const [rule, setRule] = useState(
    initialRule ? ruleToDraft(initialRule) : DEFAULT_RULE_DRAFT,
  );
  const [nameError, setNameError] = useState<string>();
  const [ruleErrors, setRuleErrors] = useState<GuaranteeRuleErrors>({});

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = draftToRule(rule);
    const errors = validateGuaranteeRule(parsed);
    const missingName = name.trim() ? undefined : 'Dê um nome à campanha.';
    setNameError(missingName);
    setRuleErrors(errors);
    if (missingName || Object.keys(errors).length > 0) return;
    create.mutate(
      { name: name.trim(), rule: parsed },
      {
        onSuccess: (campaign) => {
          onClose();
          navigate(`/guarantee/campaigns/${campaign.id}`);
        },
      },
    );
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <Input
        id="guarantee-campaign-name"
        label="Nome"
        autoFocus
        maxLength={120}
        value={name}
        error={nameError}
        readOnly={create.isPending}
        onChange={(e) => setName(e.target.value)}
      />
      <GuaranteeRuleFields
        idPrefix="guarantee-campaign-new"
        value={rule}
        onChange={setRule}
        errors={ruleErrors}
        readOnly={create.isPending}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={create.isPending}
          onClick={onClose}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Criando…' : 'Criar campanha'}
        </Button>
      </div>
    </form>
  );
}
